import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { 
  Calendar, Users, CreditCard, FileText, Mail, Settings, 
  Eye, Download, ChevronRight, TrendingUp, ExternalLink, Newspaper
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminContentManager from '../components/AdminContentManager';
import AdminCalendar from '../components/AdminCalendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, isAdmin } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    
    // Check for calendar connection status
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
    } else if (searchParams.get('calendar_error') === 'true') {
      toast.error('Failed to connect Google Calendar');
    }
    
    fetchAllData();
  }, [isAdmin]);

  const fetchAllData = async () => {
    try {
      const [statsRes, appointmentsRes, clientsRes, paymentsRes, formsRes, contactsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/intake-forms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/email-logs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data);
      setPayments(paymentsRes.data);
      setIntakeForms(formsRes.data);
      setContacts(contactsRes.data);
      setEmailLogs(emailsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const viewIntakeForm = async (formId) => {
    try {
      const response = await axios.get(`${API}/admin/intake-forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedForm(response.data);
      setFormDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load intake form');
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to get calendar authorization URL');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: 'bg-green-100 text-green-700',
      pending_payment: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      new: 'bg-blue-100 text-blue-700',
      responded: 'bg-green-100 text-green-700'
    };
    return variants[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-pulse text-muted-foreground">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-muted/30" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-light mb-2">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage appointments, clients, and practice settings</p>
            </div>
            <Button
              variant="outline"
              onClick={connectGoogleCalendar}
              className="rounded-full"
              data-testid="connect-calendar-btn"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Connect Google Calendar
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-5 w-5 text-primary" />
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
              <p className="text-2xl font-semibold">{stats?.total_clients || 0}</p>
              <p className="text-sm text-muted-foreground">Total Clients</p>
            </div>
            <div className="bg-card rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-semibold">{stats?.confirmed_appointments || 0}</p>
              <p className="text-sm text-muted-foreground">Confirmed Sessions</p>
            </div>
            <div className="bg-card rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-semibold">{intakeForms?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Intake Forms</p>
            </div>
            <div className="bg-card rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-semibold">£{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
            </div>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="calendar" className="space-y-6">
            <TabsList className="bg-card rounded-xl p-1">
              <TabsTrigger value="calendar" className="rounded-lg" data-testid="tab-calendar">
                <Calendar className="h-4 w-4 mr-2" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="appointments" className="rounded-lg" data-testid="tab-appointments">
                <Calendar className="h-4 w-4 mr-2" />
                Appointments
              </TabsTrigger>
              <TabsTrigger value="clients" className="rounded-lg" data-testid="tab-clients">
                <Users className="h-4 w-4 mr-2" />
                Clients
              </TabsTrigger>
              <TabsTrigger value="payments" className="rounded-lg" data-testid="tab-payments">
                <CreditCard className="h-4 w-4 mr-2" />
                Payments
              </TabsTrigger>
              <TabsTrigger value="forms" className="rounded-lg" data-testid="tab-forms">
                <FileText className="h-4 w-4 mr-2" />
                Intake Forms
              </TabsTrigger>
              <TabsTrigger value="contacts" className="rounded-lg" data-testid="tab-contacts">
                <Mail className="h-4 w-4 mr-2" />
                Contacts
              </TabsTrigger>
              <TabsTrigger value="discovery" className="rounded-lg" data-testid="tab-discovery">
                <Newspaper className="h-4 w-4 mr-2" />
                Discovery
              </TabsTrigger>
            </TabsList>

            {/* Calendar Tab */}
            <TabsContent value="calendar">
              <AdminCalendar />
            </TabsContent>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Client</th>
                        <th className="text-left p-4 font-medium">Service</th>
                        <th className="text-left p-4 font-medium">Date & Time</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.slice(0, 20).map((apt) => (
                        <tr key={apt.id} className="border-t border-border" data-testid={`apt-row-${apt.id}`}>
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{apt.client_name}</p>
                              <p className="text-sm text-muted-foreground">{apt.client_email}</p>
                            </div>
                          </td>
                          <td className="p-4">{apt.service_name}</td>
                          <td className="p-4">
                            {format(parseISO(apt.start_time), 'dd MMM yyyy, HH:mm')}
                          </td>
                          <td className="p-4">
                            <Badge className={getStatusBadge(apt.status)}>
                              {apt.status.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={getStatusBadge(apt.payment_status)}>
                              {apt.payment_status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Name</th>
                        <th className="text-left p-4 font-medium">Email</th>
                        <th className="text-left p-4 font-medium">Phone</th>
                        <th className="text-left p-4 font-medium">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id} className="border-t border-border" data-testid={`client-row-${client.id}`}>
                          <td className="p-4 font-medium">
                            {client.first_name} {client.last_name}
                          </td>
                          <td className="p-4">{client.email}</td>
                          <td className="p-4">{client.phone || '-'}</td>
                          <td className="p-4">
                            {format(parseISO(client.created_at), 'dd MMM yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Client</th>
                        <th className="text-left p-4 font-medium">Amount</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id} className="border-t border-border" data-testid={`payment-row-${payment.id}`}>
                          <td className="p-4">{payment.client_email}</td>
                          <td className="p-4">£{payment.amount?.toFixed(2)}</td>
                          <td className="p-4">
                            <Badge className={getStatusBadge(payment.payment_status)}>
                              {payment.payment_status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {format(parseISO(payment.created_at), 'dd MMM yyyy, HH:mm')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Intake Forms Tab */}
            <TabsContent value="forms">
              <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Client Name</th>
                        <th className="text-left p-4 font-medium">Date of Birth</th>
                        <th className="text-left p-4 font-medium">Submitted</th>
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {intakeForms.map((form) => (
                        <tr key={form.id} className="border-t border-border" data-testid={`form-row-${form.id}`}>
                          <td className="p-4 font-medium">{form.full_name}</td>
                          <td className="p-4">{form.date_of_birth}</td>
                          <td className="p-4">
                            {format(parseISO(form.completed_at), 'dd MMM yyyy')}
                          </td>
                          <td className="p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewIntakeForm(form.id)}
                              data-testid={`view-form-${form.id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Name</th>
                        <th className="text-left p-4 font-medium">Email</th>
                        <th className="text-left p-4 font-medium">Message</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="border-t border-border" data-testid={`contact-row-${contact.id}`}>
                          <td className="p-4 font-medium">{contact.name}</td>
                          <td className="p-4">{contact.email}</td>
                          <td className="p-4 max-w-xs truncate">{contact.message}</td>
                          <td className="p-4">
                            <Badge className={getStatusBadge(contact.status)}>
                              {contact.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {format(parseISO(contact.created_at), 'dd MMM yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Discovery Content Tab */}
            <TabsContent value="discovery">
              <AdminContentManager />
            </TabsContent>
          </Tabs>

          {/* Email Logs Section */}
          <div className="mt-8">
            <h2 className="font-heading text-2xl mb-4">Recent Email/SMS Logs (MOCKED)</h2>
            <div className="bg-card rounded-2xl shadow-sm p-6">
              {emailLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No email logs yet</p>
              ) : (
                <div className="space-y-4">
                  {emailLogs.slice(0, 10).map((log) => (
                    <div key={log.id} className="border-b border-border pb-4 last:border-0" data-testid={`email-log-${log.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{log.subject}</p>
                          <p className="text-sm text-muted-foreground">To: {log.to_email}</p>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-700">{log.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{log.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Intake Form Dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Intake Form - {selectedForm?.full_name}</DialogTitle>
          </DialogHeader>
          {selectedForm && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{selectedForm.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{selectedForm.date_of_birth}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{selectedForm.address}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedForm.phone}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Emergency Contact</h4>
                <p>{selectedForm.emergency_contact_name} - {selectedForm.emergency_contact_phone}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">GP Information</h4>
                <p>{selectedForm.gp_name || 'Not provided'}</p>
                <p className="text-sm text-muted-foreground">{selectedForm.gp_address || ''}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Current Medications</h4>
                <p>{selectedForm.current_medications || 'None listed'}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Mental Health History</h4>
                <p>{selectedForm.mental_health_history || 'Not provided'}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Reason for Seeking Therapy</h4>
                <p>{selectedForm.reason_for_seeking_therapy}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Previous Therapy</h4>
                <p>{selectedForm.previous_therapy || 'None'}</p>
              </div>

              {selectedForm.signature && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Signature</h4>
                  <img 
                    src={selectedForm.signature} 
                    alt="Client signature" 
                    className="border rounded-lg max-w-sm"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
